import { Component, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ApiPage, ApiUser, PagesApi } from '@tytapp/api';
import { CanonicalURLService, HostApi, OpenGraph, PageComponent } from '@tytapp/common';
import { CONTENT_TYPE_HELPER, CURRENT_PAGE_PROVIDER } from '@tytapp/content';
import { environment } from '@tytapp/environment';
import { isClientSide } from '@tytapp/environment-utils';
import { UserService } from '@tytapp/user';
import { combineLatest } from 'rxjs';

@Component({
    selector: 'app-page-view',
    templateUrl: './page-view.component.html',
    styleUrls: ['./page-view.component.scss'],
    providers: [
        { provide: CURRENT_PAGE_PROVIDER, useExisting: PageViewComponent }
    ]
})
export class PageViewComponent extends PageComponent {
    private canonicalService = inject(CanonicalURLService);
    private pagesApi = inject(PagesApi);
    private userService = inject(UserService);
    private snackBar = inject(MatSnackBar);
    private hostApi = inject(HostApi);
    private opengraph = inject(OpenGraph);

    readonly ContentTypes = CONTENT_TYPE_HELPER;

    readonly simulatorComponent = () => import('../../../../content-editor').then(m => m.ConditionSimulatorComponent);

    /**
     * True if we should load this content using a frame to the canonical
     * web root. This is used for native apps so that they do not need to update
     * in order to use the latest features of the Pages system.
     */
    useFrame = false;

    canonicalUrl: string;

    get frameUrl() {
        let baseUrl = environment.urls.webRoot;

        // Used to make it easier to test the integration
        if (environment.name === 'development' && isClientSide()) {
            baseUrl = location.origin;
        }

        return `${baseUrl}${this.canonicalUrl}?chromeless=1&skipFrame=1`;
    }

    _fetchedPage: ApiPage;
    _preview: ApiPage;

    pageId: string;
    user: ApiUser;

    private _editorSync = false;
    get editorSync() { return this._editorSync; }
    set editorSync(value) {
        if (this._editorSync !== value) {
            this._editorSync = value;
            if (value)
                this.requestEditChanges();
        }
    }

    syncMessageDismissed = false;
    unpublishMessageDismissed = false;
    syncChannel: BroadcastChannel;

    refreshing = false;

    overridesChanged = () => {
        this.snackBar.open('Rerendering...', undefined, { duration: 3000 });
        this.refreshing = true;
        setTimeout(() => this.refreshing = false, 100);
    };

    get page() {
        if (this.user?.staff && this.editorSync)
            return this._preview ?? this._fetchedPage;
        return this._fetchedPage;
    }

    requestEditChanges() {
        if (this.syncChannel)
            this.syncChannel.postMessage(JSON.stringify({ type: 'subscribe' }));
    }

    async init() {
        this.subscribe(this.userService.userChanged, user => this.user = user);
        await this.userService.ready;

        this.subscribe(combineLatest([ this.route.params, this.route.queryParams ]), async ([params, qp]) => {
            let shouldUseFrame = await this.hostApi.hasCapability('pages:frame_pages');

            if (qp.skipFrame !== '1' && shouldUseFrame) {
                this.useFrame = true;
            }

            this.pageId = params.id;
            this._fetchedPage = await this.pagesApi.getPage(params.id).toPromise();
            this.pageTitle = this._fetchedPage?.title;
            this.editorSync = qp.mode === 'editor-sync';

            if (this._fetchedPage) {
                this.opengraph.setLdJson({
                    "@context": "http://schema.org",
                    "@type": "WebPage",
                    "name": this.pageTitle,
                    "url": `${environment.urls.webRoot}${this._fetchedPage?.url}`,
                    "title": this._fetchedPage?.title,
                    "description": this._fetchedPage?.description

                });
                this.opengraph.setMetadata({
                    siteName: "TYT.com",
                    title: this._fetchedPage.title,
                    description: this._fetchedPage.description,
                    image: "/assets/touch/icon-1400.png"
                })
                this.meta.updateTag({ content: this._fetchedPage.summary ?? this._fetchedPage.description }, "name='description'");
            }

            if (this.syncChannel) {
                this.syncChannel.close();
                this.syncChannel = undefined;
            }

            this.unpublishMessageDismissed = false;
            this.syncChannel = new BroadcastChannel(`page-${this.pageId}`);
            this.syncChannel.addEventListener('message', ev => {
                let message = JSON.parse(ev.data);
                if (message.type === 'change') {
                    console.log(`Received update from editor.`);
                    this._preview = message.page;
                }
            });
            this.requestEditChanges();

            this.canonicalUrl = this._fetchedPage.url;

            if (!this.editorSync && !this.useFrame) {
                if (this.canonicalService.redirectToCanonical(this._fetchedPage.url)) {
                    return;
                }
            }
        });
    }
}
